/* tslint:disable */
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpRequest,
  HttpResponse,
  HttpHeaders,
} from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { ApiConfiguration as __Configuration } from '../api-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';

import { ChatStarterMetadata } from '../models/chat-starter-metadata';
import { Chat } from '../models/chat';
import { ChatMessage } from '../models/chat-message';
import { ChatRequest } from '../models/chat-request';
import { PageChat } from '../models/page-chat';
import { CreateChatRequest } from '../models/create-chat-request';
import { ChatContext } from '../models/chat-context';
import { K8sAnalyzeResponse } from '../models/k8s-analyze-response';

/**
 * Endpoints for Chat GPT operations
 */
@Injectable({
  providedIn: 'root',
})
class UiChatGptControllerService extends __BaseService {
  static readonly getAllStartersUsingGETPath =
    '/cc-ui/v1/clusters/chat/metadata';
  static readonly getChatByIdUsingGETPath = '/cc-ui/v1/clusters/chat/{chatId}';
  static readonly chatUsingPOSTPath = '/cc-ui/v1/clusters/chat/{chatId}';
  static readonly getAllChatsUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/chat';
  static readonly createChatUsingPOSTPath =
    '/cc-ui/v1/clusters/{clusterId}/chat';
  static readonly getK8sChatsUsingPOSTPath =
    '/cc-ui/v1/clusters/{clusterId}/k8s-chat';
  static readonly analyzeKubernetesClusterUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/kubernetes/analyze';

  constructor(config: __Configuration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Get chat starters metadata
   * @return Chat metadata found
   */
  getAllStartersUsingGETResponse(): __Observable<
    __StrictHttpResponse<Array<ChatStarterMetadata>>
  > {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/cc-ui/v1/clusters/chat/metadata`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<ChatStarterMetadata>>;
      }),
    );
  }
  /**
   * Get chat starters metadata
   * @return Chat metadata found
   */
  getAllStartersUsingGET(): __Observable<Array<ChatStarterMetadata>> {
    return this.getAllStartersUsingGETResponse().pipe(
      __map((_r) => _r.body as Array<ChatStarterMetadata>),
    );
  }

  /**
   * Get chat by ID
   * @param chatId Chat ID
   * @return Chat found
   */
  getChatByIdUsingGETResponse(
    chatId: string,
  ): __Observable<__StrictHttpResponse<Chat>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/chat/${encodeURIComponent(String(chatId))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Chat>;
      }),
    );
  }
  /**
   * Get chat by ID
   * @param chatId Chat ID
   * @return Chat found
   */
  getChatByIdUsingGET(chatId: string): __Observable<Chat> {
    return this.getChatByIdUsingGETResponse(chatId).pipe(
      __map((_r) => _r.body as Chat),
    );
  }

  /**
   * Send a message to a chat
   * @param params The `UiChatGptControllerService.ChatUsingPOSTParams` containing the following parameters:
   *
   * - `request`: request
   *
   * - `chatId`: Chat ID
   *
   * @return Message sent successfully
   */
  chatUsingPOSTResponse(
    params: UiChatGptControllerService.ChatUsingPOSTParams,
  ): __Observable<__StrictHttpResponse<ChatMessage>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = params.request;

    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/clusters/chat/${encodeURIComponent(String(params.chatId))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<ChatMessage>;
      }),
    );
  }
  /**
   * Send a message to a chat
   * @param params The `UiChatGptControllerService.ChatUsingPOSTParams` containing the following parameters:
   *
   * - `request`: request
   *
   * - `chatId`: Chat ID
   *
   * @return Message sent successfully
   */
  chatUsingPOST(
    params: UiChatGptControllerService.ChatUsingPOSTParams,
  ): __Observable<ChatMessage> {
    return this.chatUsingPOSTResponse(params).pipe(
      __map((_r) => _r.body as ChatMessage),
    );
  }

  /**
   * Get chats by cluster ID
   * @param clusterId Cluster ID
   * @return Chats found
   */
  getAllChatsUsingGETResponse(
    clusterId: string,
  ): __Observable<__StrictHttpResponse<PageChat>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(String(clusterId))}/chat`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<PageChat>;
      }),
    );
  }
  /**
   * Get chats by cluster ID
   * @param clusterId Cluster ID
   * @return Chats found
   */
  getAllChatsUsingGET(clusterId: string): __Observable<PageChat> {
    return this.getAllChatsUsingGETResponse(clusterId).pipe(
      __map((_r) => _r.body as PageChat),
    );
  }

  /**
   * Create a new chat
   * @param params The `UiChatGptControllerService.CreateChatUsingPOSTParams` containing the following parameters:
   *
   * - `request`: request
   *
   * - `clusterId`: Cluster ID
   *
   * @return OK or Chat created successfully
   */
  createChatUsingPOSTResponse(
    params: UiChatGptControllerService.CreateChatUsingPOSTParams,
  ): __Observable<__StrictHttpResponse<Chat | Chat>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = params.request;

    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/chat`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Chat | Chat>;
      }),
    );
  }
  /**
   * Create a new chat
   * @param params The `UiChatGptControllerService.CreateChatUsingPOSTParams` containing the following parameters:
   *
   * - `request`: request
   *
   * - `clusterId`: Cluster ID
   *
   * @return OK or Chat created successfully
   */
  createChatUsingPOST(
    params: UiChatGptControllerService.CreateChatUsingPOSTParams,
  ): __Observable<Chat | Chat> {
    return this.createChatUsingPOSTResponse(params).pipe(
      __map((_r) => _r.body as Chat | Chat),
    );
  }

  /**
   * Ask Questions about k8s operations
   * @param params The `UiChatGptControllerService.GetK8sChatsUsingPOSTParams` containing the following parameters:
   *
   * - `questions`: questions
   *
   * - `clusterId`: Cluster ID
   *
   * @return Chats found
   */
  getK8sChatsUsingPOSTResponse(
    params: UiChatGptControllerService.GetK8sChatsUsingPOSTParams,
  ): __Observable<__StrictHttpResponse<ChatMessage>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = params.questions;

    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/k8s-chat`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<ChatMessage>;
      }),
    );
  }
  /**
   * Ask Questions about k8s operations
   * @param params The `UiChatGptControllerService.GetK8sChatsUsingPOSTParams` containing the following parameters:
   *
   * - `questions`: questions
   *
   * - `clusterId`: Cluster ID
   *
   * @return Chats found
   */
  getK8sChatsUsingPOST(
    params: UiChatGptControllerService.GetK8sChatsUsingPOSTParams,
  ): __Observable<ChatMessage> {
    return this.getK8sChatsUsingPOSTResponse(params).pipe(
      __map((_r) => _r.body as ChatMessage),
    );
  }

  /**
   * analyzeKubernetesCluster
   * @param params The `UiChatGptControllerService.AnalyzeKubernetesClusterUsingGETParams` containing the following parameters:
   *
   * - `filters`: filters
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  analyzeKubernetesClusterUsingGETResponse(
    params: UiChatGptControllerService.AnalyzeKubernetesClusterUsingGETParams,
  ): __Observable<__StrictHttpResponse<K8sAnalyzeResponse>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    (params.filters || []).forEach((val) => {
      if (val != null) __params = __params.append('filters', val.toString());
    });

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/kubernetes/analyze`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<K8sAnalyzeResponse>;
      }),
    );
  }
  /**
   * analyzeKubernetesCluster
   * @param params The `UiChatGptControllerService.AnalyzeKubernetesClusterUsingGETParams` containing the following parameters:
   *
   * - `filters`: filters
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  analyzeKubernetesClusterUsingGET(
    params: UiChatGptControllerService.AnalyzeKubernetesClusterUsingGETParams,
  ): __Observable<K8sAnalyzeResponse> {
    return this.analyzeKubernetesClusterUsingGETResponse(params).pipe(
      __map((_r) => _r.body as K8sAnalyzeResponse),
    );
  }
}

module UiChatGptControllerService {
  /**
   * Parameters for chatUsingPOST
   */
  export interface ChatUsingPOSTParams {
    /**
     * request
     */
    request: ChatRequest;

    /**
     * Chat ID
     */
    chatId: string;
  }

  /**
   * Parameters for createChatUsingPOST
   */
  export interface CreateChatUsingPOSTParams {
    /**
     * request
     */
    request: CreateChatRequest;

    /**
     * Cluster ID
     */
    clusterId: string;
  }

  /**
   * Parameters for getK8sChatsUsingPOST
   */
  export interface GetK8sChatsUsingPOSTParams {
    /**
     * questions
     */
    questions: Array<ChatContext>;

    /**
     * Cluster ID
     */
    clusterId: string;
  }

  /**
   * Parameters for analyzeKubernetesClusterUsingGET
   */
  export interface AnalyzeKubernetesClusterUsingGETParams {
    /**
     * filters
     */
    filters: Array<string>;

    /**
     * clusterId
     */
    clusterId: string;
  }
}

export { UiChatGptControllerService };
